import React, { useEffect, useState, useRef } from "react";
// import Pen from "../pen.svg";
import ReactPlayer from "react-player";
import SlidingTextBackground from "./SlidingTextBackground";
import { gsap, Power3, Back, Bounce, Elastic } from "gsap";
import { ScrollTrigger } from "gsap/all";
import { fadeIn, flyInFromRight, rotateInFromRight } from "./hooks/animations";
import FlyInText from "./FlyInText";
import { Link } from "react-router-dom";
import useLargeScreen from "./hooks/useLargeScreen";
import CalligraphyIllustration from "./CalligraphyIllustration";

// import FillInText from "./FillInText";
gsap.registerPlugin(ScrollTrigger);

const DEV = false;

const Hero = ({ callback = (f) => f }) => {
  const isLargeScreen = useLargeScreen();

  const wrapper = useRef(null);

  const firstRender = useRef(true);
  const mobileTimeline = useRef(null);
  const desktopTimeline = useRef(null);
  const desktopScrollTrigger = useRef(null);

  const log = (a) => DEV && console.log(a);

  ScrollTrigger.defaults({ ease: "power3.out" });
  const q = gsap.utils.selector(wrapper);

  const defaultAnimations = () => {
    // flyInFromRight(q(".hero-content-wrapper > h1"), false);
    // fadeIn(q(".hero-content-wrapper > p"), false);
    // q(".card > *").forEach((e) => fadeIn(e));
    gsap.from(q(".hero-content-wrapper > h1"), {
      opacity: 0,
      y: -90,
      delay: 0.15,
      ease: Power3.easeInOut(),
      duration: 0.55,
      scrollTrigger: {
        trigger: q(".hero-content-wrapper > h1"),
        toggleActions: "restart none restart none",
      },
    });
    gsap.from(q(".calligraphy-illustration > *"), {
      opacity: 0,
      y: 180,
      delay: 0.15,
      stagger: 0.2,
      ease: Power3.easeInOut(),
      duration: 0.45,
      scrollTrigger: {
        trigger: q(".hero-content-wrapper > h1"),
        toggleActions: "restart none restart none",
      },
    });
    gsap.from(q(".hero-content-wrapper > p"), {
      opacity: 0,
      delay: 0.2,
      x: 100,

      duration: 0.45,
      scrollTrigger: {
        trigger: q(".hero-content-wrapper > p"),
        toggleActions: "restart none restart none",
      },
    });
    gsap.from(q(".highlight"), {
      width: 0,
      delay: 0.1,
      scrollTrigger: {
        trigger: q(".highlight"),
        start: "bottom bottom", // when the top of the trigger hits the top of the viewport
        end: "+=400", // end after scrolling 500px beyond the start
        scrub: 0.5, // smooth scrubbing, takes 1 second to "catch up" to the scrollbar
      },
    });
    gsap.from(q(".intro > p"), {
      opacity: 0,
      x: 120,

      // delay: 0.2,
      scrollTrigger: {
        trigger: q(".intro > p"),
        start: "top bottom",
        end: "top center",
        scrub: 0.2,
      },
    });
    gsap.from(q(".rotate-in"), {
      x: 140,
      y: 20,
      opacity: 0.25,
      scrollTrigger: {
        trigger: q(".rotate-in"),
        start: "60px bottom",
        end: "+=200",
        scrub: 1,
      },
    });
    gsap.from(q(".appear"), {
      opacity: 0,
      delay: 0.1,
      duration: 0.75,
      stagger: 0.2,
      scrollTrigger: {
        trigger: q(".appear"),
        start: "top center",
        // endTrigger: q("video"),
        // end: "top 80%",
        // scrub: 0.5,
        toggleActions: "restart play play reset",
      },
    });
    gsap.to(q(".video-curtain"), {
      width: 0,
      delay: 0.2,
      scrollTrigger: {
        trigger: q(".video-curtain"),
        start: "100px bottom",
        end: "+=250",
        scrub: 1,
      },
    });

    callback();
  };

  const removeDesktopAnimationTimeline = () => {
    if (desktopTimeline.current != null) {
      log("reverted desktop timeline");
      desktopTimeline.current.revert();
      desktopTimeline.current = null;
    }
    if (desktopScrollTrigger.current != null) {
      log("reverted desktop scrolltrigger");
      desktopScrollTrigger.current.kill({ allowAnimation: true });
      desktopScrollTrigger.current = null;
    }
  };
  const revertMobileAnimationTimeline = () => {
    if (mobileTimeline.current != null) {
      log("reverted mobile animations");
      mobileTimeline.current.seek("end");
      mobileTimeline.current.pause();
      mobileTimeline.current.revert();
      mobileTimeline.current = null;
    }
  };
  const createDesktopAnimationTimeline = () => {
    // gsap.to(".mask-heart", {
    //   scale: 1,
    //   opacity: 1,
    //   rotate: 0,
    // });
    // gsap.to(".mask-parallelogram-3", {
    //   scale: 1,
    //   opacity: 0,
    //   rotate: 0,
    // });

    const tl = gsap.timeline();
    const disappear = {
      opacity: 0,
      scale: 0.2,
      duration: 1,
    };

    // ANIMATION OVERVIEW FOR MY CLUELESS FUTURE SELF
    // in, in, out, out,
    // in, in, out, out

    // move container down
    tl
      // .addLabel("start-animations", ">")
      .to(q(".features-wrapper"), {
        yPercent: 55,
      })
      // bring in feature 1 content
      .from(q(".feature-1-content"), {
        ease: "power3.out",
        scale: 0.8,
      })
      // bring in feature 1 illustration
      .from(
        q(
          ".feature-1-illustration .mask-heart, .feature-1-illustration .heart"
        ),
        {
          stagger: 0.25,
          yPercent: 100,
          ...disappear,
          ease: "power2.out",
        },
        ">-25%"
      )
      .addLabel("bring-in-feature-1-illustration", ">")

      .to(q(".feature-1-content > *"), {
        // remove feature 1 content
        yPercent: -150,
        ...disappear,
        ease: "power3.out",
      })
      .to(
        q(".feature-1-illustration > *"), // remove feature 1 illustration
        {
          xPercent: 150,
          ...disappear,
          ease: "power3.out",
          rotate: -45,
          stagger: 0.1,
        },
        ">-50%"
      )
      .to(".feature-2", {
        zIndex: 100,
      })
      .from(
        q(".feature-2-content > *"),
        {
          xPercent: -150,
          ...disappear,
          ease: "power3.out",
        },
        ">-100%"
      )
      .from(
        q(".feature-2-illustration > *"),
        {
          yPercent: 150,
          stagger: 0.2,
          zIndex: 0,
          ...disappear,
          ease: "power2.out",
        },
        ">-100%"
      )
      .addLabel("bring-in-feature-2-illustration", ">")

      .to(q(".feature-2-content"), {
        yPercent: -150,
        ...disappear,
        ease: "power3.out",
      })
      .to(
        q(".feature-2-illustration > *"),
        {
          yPercent: -150,
          stagger: 0.2,
          ...disappear,
          ease: "power3.out",
        },
        ">-75%"
      )
      .to(q(".features-wrapper"), {
        yPercent: -80,
      })
      // .to(
      //   q(".features-wrapper"),
      //   {
      //     height: 0,
      //   },
      //   ">-25%"
      // )

      .addLabel("end", ">");

    desktopTimeline.current = tl;

    desktopScrollTrigger.current = ScrollTrigger.create({
      animation: tl,
      trigger: q(".features-wrapper"),
      // start: "bottom center", // when the top of the trigger hits the top of the viewport
      start: "center top",
      // end: "bottom",
      // end: "bottom 50%",
      // end: "+=300%",
      end: "+=5000",
      scrub: 0.5,
      pin: true,
      pinSpacing: false,
      snap: {
        snapTo: "labelsDirectional", // snap to the closest label in the timeline
        duration: { min: 0.75, max: 1.2 }, // the snap animation should be at least 0.2 seconds, but no more than 3 seconds (determined by velocity)
        delay: 0.015, // wait x seconds from the last scroll event before doing the snapping
        ease: "power3.out", // the ease of the snap animation ("power3" by default)
      },
      // onLeave: () => {
      //   featuresWrapper.current.
      // }
    });
  };
  const createMobileAnimationTimeline = () => {
    const tl = gsap.timeline();

    tl.from(q(".mask-heart"), {
      y: 400,
      stagger: 0.3,
      scale: 0.2,
      opacity: 0.6,
      // delay: 0.2,
      scrollTrigger: {
        trigger: q(".gifts-heading"),
        start: "50px bottom", // when the top of the trigger hits the top of the viewport
        // endTrigger: q(".wallpapers-heading"),
        end: "+=320px",
        // end after scrolling 500px beyond the start
        // smooth scrubbing, takes 1 second to "catch up" to the scrollbar
        scrub: 1.05,
      },
    })
      .from(q(".mask-parallelogram-3"), {
        y: 250,
        scale: 0.5,
        stagger: 0.25,
        opacity: 0.75,
        scrollTrigger: {
          trigger: q(".mask-parallelogram-3"),
          start: "top bottom", // when the top of the trigger hits the top of the viewport
          end: "+=160px",

          scrub: 1.05,
        },
      })
      .addLabel("end", ">");
    // .to(q(".mask-parallelogram-3"), {
    //   backgroundColor: "red",
    // });

    mobileTimeline.current = tl;
  };

  useEffect(() => {
    console.log("screen size changed, large screen ?", isLargeScreen);
    if (!isLargeScreen) {
      log(desktopTimeline, desktopTimeline.current);
      // removeDesktopAnimationTimeline();
      createMobileAnimationTimeline();
    } else {
      log(mobileTimeline, mobileTimeline.current);
      // revertMobileAnimationTimeline();
      createDesktopAnimationTimeline();
    }
    if (firstRender.current) {
      log("rendering universal animations");
      defaultAnimations();
      firstRender.current = false;
    }
    return () => {
      log("oh no!");
      revertMobileAnimationTimeline();
      removeDesktopAnimationTimeline();
    };
  }, [isLargeScreen]);

  return (
    <div ref={wrapper}>
      <section className="section-padding dots-bg-small min-h-[70vh] py-16 pb-20 sm:pb-24 lg:grid lg:grid-cols-12 ">
        <div className="lg:col-span-7 hero-content-wrapper">
          <h1
            id="hero-heading"
            className="sm:mb-12 sm:mt-6 2xl:text-7xl font-black font-primary mb-7 mt-1 text-6xl tracking-tight sm:text-7xl md:text-8xl lg:tracking-tighter "
          >
            Custom Calligraphy
          </h1>
          <p className="section-desc mt-2 sm:-mt-3 sm:mb- ">
            Pick any word or phrase you'd love illustrated and we'll design a
            custom font handcrafted for your artwork.
          </p>
          <Link to={"/products"}>
            <button className="grid pen-button transition lg:hover:shadow-xl  max-w-full grid-cols-3 relative w-max btn btn-primary rounded-md pr-8 md:px-8">
              <div className="w-12 h-12 -translate-x-4 -translate-y-4 diff">
                <svg
                  viewBox="0 0 100 100"
                  className="diff w-full h-full"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    className="diff"
                    d="M50.1543 71.9956C49.9951 71.8486 49.8701 71.666 49.7866 71.4583C49.703 71.2506 56.3358 64.4651 56.3406 64.2284C56.3455 63.9917 37.7627 52.0732 37.8553 51.8437C37.9478 51.6141 2.78206 13.0229 2.94815 12.8356C3.11384 12.648 46.0258 43.0978 46.2398 42.9808C46.4539 42.8638 60.3032 59.8254 60.5329 59.7969C60.7627 59.7685 66.1792 52.6297 66.3896 52.6942C66.5999 52.7586 66.7899 52.8677 66.9487 53.0152L73.4524 59.0375L84.9644 60.5878C85.841 60.7055 86.6404 61.0644 87.2843 61.6291C87.9281 62.1938 88.3943 62.9449 88.6371 63.8089L98.7157 99.697L62.8035 93.0017C61.9401 92.8401 61.1641 92.4403 60.5516 91.8416C59.939 91.2429 59.5109 90.4657 59.3091 89.5862L56.6615 78.0212C56.6615 78.0212 50.3136 72.1427 50.1543 71.9956ZM60.1798 77.2157L62.7155 88.292C62.7827 88.5861 62.9258 88.8459 63.1306 89.0458C63.3355 89.2458 63.595 89.3791 63.8838 89.4325L93.6139 94.9765L85.2693 65.2562C85.1878 64.9695 85.0325 64.7203 84.8183 64.5329C84.6042 64.3455 84.3385 64.2263 84.0473 64.187L73.0182 62.7026L60.1763 77.2161L60.1798 77.2157Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M93.5295 94.6747L79.1418 77.9045C79.1564 77.1927 78.9658 76.5222 78.5941 75.9779C78.2224 75.4336 77.6863 75.0399 77.0536 74.8465C76.4209 74.6532 75.7199 74.6689 75.0395 74.8917C74.359 75.1145 73.7295 75.5343 73.2306 76.0981C72.7317 76.662 72.3859 77.3444 72.2367 78.0592C72.0876 78.7741 72.1419 79.4891 72.3928 80.114C72.6437 80.7389 73.0799 81.2455 73.6463 81.5698C74.2126 81.8941 74.8837 82.0216 75.5746 81.936L93.3155 94.9166L93.614 94.9729L93.5295 94.6747Z"
                    fill="white"
                  />
                </svg>
              </div>
              <span className="col-start-2 col-span-2 -ml-8 w-max font-secondary font-bold text-white">
                Create My MasterPiece
              </span>
            </button>
          </Link>
        </div>
        <div className="hidden lg:block lg:col-start-9 lg:col-span-5 ">
          <CalligraphyIllustration className="w-full calligraphy-illustration" />
        </div>
      </section>{" "}
      <section
        id="about"
        className="about bg-primary section-padding relative dots-bg-small h-max min-h-screen  overflow-y-hidden py-12 "
      >
        <div className="md:flex md:gap-4 md:place-items-center">
          <div className="intro">
            <h1 className="section-heading-alt mb-4">
              <span className="highlight ">Your</span> Text
            </h1>
            <h1 className="section-heading-alt mb-10">
              Our <span className="highlight">Art</span>
            </h1>
            <p className="section-desc-alt">
              We’ll illustrate whatever you’d like! No inspiration? No problem,
              we’ve got you covered with readymade prompts to help you get
              started instantly.{" "}
            </p>
          </div>
          <div className=" h-56 w-full bg-transparent relative overflow-hidden mask mask-circle">
            <SlidingTextBackground
              classes="rotate-in"
              text={["Custom", "Calligraphy"]}
            />
          </div>
        </div>
        <h1 className="section-heading-alt mt-10 mb-0 sm:mt-20 appear">
          Handmade.
        </h1>
        <p className="section-heading-alt appear">Just for you.</p>
        <div className="w-full video-wrapper h-auto mt-8 shadow-lg relative p-0">
          <ReactPlayer
            muted={true}
            playing={true}
            loop={true}
            playbackRate={1.5}
            url="assets/demo 2.mp4"
            className="animate-curtain"
            config={{
              file: {
                attributes: {
                  style: {
                    borderRadius: "0.5rem",
                  },
                },
              },
            }}
          />
          <section className="absolute video-curtain bg-primary w-full h-full top-0 left-0"></section>
        </div>
      </section>
      <div className="features-wrapper section-padding py-10 lg:relative md:py-10 lg:p-0 lg:z-50 lg:bg-secondary lg:w-full  h-max lg:h-[100vh]  lg:overflow-visible lg:p-0">
        {" "}
        {/*lg:mb-[130%]*/}
        {/* <section className=" h-max overflow-hidden pb-16 "> */}{" "}
        {/* <div className="h-screen bg-red-900 absolute w-full"></div> */}
        <div className="lg:absolute lg:z-20  lg:section-padding lg:py-16 overflow-visible lg:top-0 lg:left-0 feature-1 flex z-50 flex-col relative items-center justify-center w-full lg:flex-row lg:align-center lg:gap-6 lg:justify-between lg:h-max ">
          <div className="feature-1-content">
            <div className="md:flex md:gap-4 md:w-min md:relative md:m-auto lg:gap-0 lg:flex-col lg:justify-start lg:ml-0">
              <FlyInText
                classes="features-heading mb-0 lg:m-0 gifts-heading"
                text={"Breathtaking"}
              />
              <FlyInText
                classes="features-heading gifts-heading lg:mt-0 "
                text={"Gifts"}
              />
            </div>
            <p className="section-desc text-center lg:text-left">
              One of a kind lettering for personalized gifts to make your
              special occasions truly memorable.
            </p>
          </div>
          <div className="relative sm:w-8/12 lg:w-full lg:-mt-2 feature-1-illustration">
            <div className="card w-full bg-base-100 shadow-xl image-full mask mask-heart relative overflow-visible z-10 ">
              <figure>
                <img src="assets/gift-image.jpg" alt="gift" />
              </figure>
              <div className="relative card-body flex items-center justify-center ">
                <Link
                  to="/products?c=physical"
                  className="btn btn-secondary underline btn-outline border-none text-lg transform -translate-y-4 md:text-xl btn-wide rounded-md md:py-2 lg:py-3 hover:shadow-2xl h-max"
                >
                  Shop Now
                </Link>
              </div>
            </div>
            <div className="absolute w-full h-full mask mask-heart bg-gradient-to-b from-transparent to-primary opacity-75 -bottom-4 heart-shadow"></div>
            <div className="absolute w-full h-full mask mask-heart bg-base-300 -bottom-2 "></div>
            <div className="heart -bottom-4 left-[65%] animate-float"></div>
            <div
              style={{ "--offset": "5.5s" }}
              className="heart -bottom-4 right-[65%] animate-float"
            ></div>
          </div>
        </div>
        <div className="feature-2 h-max overflow-visible lg:absolute lg:z-10 lg:section-padding lg:py-16 lg:overflow-hidden lg:top-0 lg:left-0 feature-2 flex z-50 flex-col relative items-center justify-center w-full lg:flex-row lg:align-center lg:gap-6 lg:justify-between mt-16">
          <div className="feature-2-content">
            <div className="md:flex md:gap-2 lg:gap-0 lg:flex-col lg:justify-start ">
              <FlyInText
                classes="features-heading mb-0 lg:m-0 "
                text={"Stunning"}
              />
              <FlyInText
                classes="features-heading lg:mt-0"
                text={"Wallpapers"}
              />
            </div>
            <p className="section-desc text-center lg:text-left">
              Handdrawn digital lettering illustrated with the aspect ratio of
              your choice!
            </p>
          </div>
          <div className="relative h-max overflow-visible w-full sm:w-8/12 lg:w-7/12 feature-2-illustration lg:z-50">
            <div className="z-20 h-max overflow-visible card w-full scale-110 bg-base-100 image-full mask mask-parallelogram-3 mt-2">
              <figure>
                <img
                  src="assets/sana.jpg"
                  alt="wallpaper"
                  className="transform -rotate-[24deg]"
                />
              </figure>
              <div className="card-body flex items-center justify-center ">
                <Link
                  to="/products?c=digital"
                  className="transform scale-[90%] btn btn-secondary underline btn-outline border-none text-lg md:text-xl btn-wide rounded-md md:py-2 lg:py-3 hover:shadow-2xl h-max"
                >
                  Shop Digital
                </Link>
              </div>
            </div>
            <div className=" absolute mask mask-parallelogram-3 w-24 h-24 bg-primary -bottom-14 left-10 shadow-inner shadow-2xl bg-primary opacity-25"></div>
            <div className=" absolute mask mask-parallelogram-3 w-24 h-24 bg-primary -top-8 right-4 shadow-inner shadow-2xl bg-primary opacity-25"></div>
            <div className="z-10 absolute w-full h-full mask bg-secondary dots-bg top-3 left-3 mask mask-parallelogram-3 sm:top-4 sm:left-4 md:top-5 md:left-5 lg:top-6 lg:left-6"></div>
            <div className="z-10 absolute w-full h-full mask bg-gradient-to-br from-base-200 top-3 left-3 mask mask-parallelogram-3 sm:top-4 sm:left-4 md:top-5 md:left-5 lg:top-6 lg:left-6"></div>
            <div className="absolute w-full h-full mask bg-primary top-5 left-5 mask-parallelogram-3 sm:top-6 sm:left-6 md:top-7 md:left-7 lg:top-8 lg:left-8"></div>
          </div>
        </div>
        {/* </section> */}
      </div>
      {/* <div className="divider lg:w-full lg:py-1"></div> */}
      {/* <div className="feature-2 flex flex-col relative items-center justify-center w-full mt-14 p-0 ">
            <div className="md:flex md:gap-2 lg:gap-4">
              <FlyInText
                classes="z-10 section-heading text-center wallpapers-heading mb-0"
                text={"Stunning"}
              />
              <FlyInText
                classes="z-10 section-heading text-center wallpapers-heading"
                text={"Wallpapers"}
              />
            </div>
            <p className="section-desc text-center">
              Handdrawn digital lettering illustrated with the aspect ratio of
              your choice!
            </p>
            
            <div className="relative w-full sm:w-8/12">
              <div className="z-20 card w-full scale-110 bg-base-100 image-full mask mask-parallelogram-3 mt-2">
                <figure>
                  <img
                    src="assets/sana.jpg"
                    alt="wallpaper"
                    className="transform -rotate-[24deg]"
                  />
                </figure>
                <div className="card-body flex items-center justify-center ">
                  <Link
                    to="/products?c=digital"
                    className="transform scale-[90%] btn btn-secondary underline btn-outline border-none text-lg md:text-xl btn-wide rounded-md md:py-2 lg:py-3 hover:shadow-2xl h-max"
                  >
                    Shop Digital
                  </Link>
                </div>
              </div>
              <div className=" absolute mask mask-parallelogram-3 w-24 h-24 bg-primary -bottom-14 left-10 shadow-inner shadow-2xl bg-primary opacity-25"></div>
              <div className=" absolute mask mask-parallelogram-3 w-24 h-24 bg-primary -top-8 right-4 shadow-inner shadow-2xl bg-primary opacity-25"></div>
              <div className="z-10 absolute w-full h-full mask bg-secondary dots-bg top-3 left-3 mask mask-parallelogram-3 sm:top-4 sm:left-4 md:top-5 md:left-5 lg:top-6 lg:left-6"></div>
              <div className="z-10 absolute w-full h-full mask bg-gradient-to-br from-base-200 top-3 left-3 mask mask-parallelogram-3 sm:top-4 sm:left-4 md:top-5 md:left-5 lg:top-6 lg:left-6"></div>
              <div className="absolute w-full h-full mask bg-primary top-5 left-5 mask-parallelogram-3 sm:top-6 sm:left-6 md:top-7 md:left-7 lg:top-8 lg:left-8"></div>
            </div>
          </div>
        </section>
      </div> */}
    </div>
  );
};

export default Hero;
